import type { Tone } from '../tone';
import { confinedToAccounts } from '../access/accountsScreens';
import type { Permission, Role, ViewKey } from '../access/role';
import { CARRIER_OTHER, CARRIER_OTHER_MAX, CARRIER_OTHER_MIN } from '../catalog/catalog';
import { daysBefore } from '../time/dateRange';
import { labDayOf } from '../time/labDay';
import type { OrderState } from './orderState';

/**
 * Shipping as the lab actually ships. Request of 13 September 2026, section 1.
 *
 * Two routes out of the building. An international case is collected by a
 * courier, and its waybill code usually arrives the next morning; a local case
 * goes in the lab's own van and never has a code at all. The person dispatching
 * says which, with a method, rather than the system guessing from the carrier's
 * name: a lab that buys a second van should not need a code change.
 *
 *   paid, then courier collects   awaiting_tracking, then in_transit once the code is typed
 *   paid, then the lab drives it  in_transit, with no code, ever
 *   either, then marked received  delivered
 *
 * `dispatched` is true from the moment the case leaves, whichever route, so
 * orderState and everything reading it are untouched: the order leaves Orders
 * for the Archive at exactly the moment it always did.
 */

export const SHIPPING_METHODS = ['courier', 'lab'] as const;
export type ShippingMethod = (typeof SHIPPING_METHODS)[number];

export const SHIPPING_STATUSES = ['pending', 'awaiting_tracking', 'in_transit', 'delivered'] as const;
export type ShippingStatus = (typeof SHIPPING_STATUSES)[number];

/** Four characters is the shortest waybill code the lab has ever seen. */
export const WAYBILL_CODE_MIN = 4;

/** A driver's name is for the log and prints nowhere. */
export const DRIVER_NAME_MAX = 60;

/** How far back the Delivered group on the shipping screen reaches, in lab days, today among them. */
export const DELIVERED_SHOWN_DAYS = 14;

/** The refusals, worded once for the mock server, the dialogs and the tests. The API sends the same. */
export const SHIPPING_REFUSAL = {
  held: 'This order is on hold and cannot be dispatched.',
  notReady:
    'This order is not ready to dispatch. Every kit must be Ready, and the order paid or released by Accounts.',
  chooseCourier: 'Choose the courier',
  nameTheCourier: 'Name the courier',
  carrierNameTooShort: `A courier's name is at least ${CARRIER_OTHER_MIN} characters`,
  carrierNameTooLong: `Keep the courier's name to ${CARRIER_OTHER_MAX} characters`,
  labHasNoCode: 'A lab delivery has no waybill code',
  notDispatched: 'This case has not been dispatched yet.',
  arrivesBeforeLeaves: 'A case cannot arrive before it leaves',
  codeTooShort: 'A waybill code is at least four characters',
  /**
   * A second Mark received on a case that already is: a second tap, or a page
   * that had not heard. Refused so the first mark keeps who made it and when; a
   * correction goes through the edit. The request of 5 October 2026 (third),
   * section 11. The API's ShippingRules.AlreadyReceived.
   */
  alreadyReceived: 'This case is already marked received.',
} as const;

/**
 * The two things the Archive says about a case that has left: Shipped until
 * somebody marks it received, Received from then on. Not a status of its own
 * and never stored: Shipped is every `ShippingStatus` of a dispatched case but
 * `delivered`, Received is `delivered`. The strings travel on the wire, as the
 * `status` filter of `GET /archive`, and are the API's ArchiveStatuses. The
 * request of 5 October 2026 (third), section 11.
 */
export const ARCHIVE_STATUSES = ['shipped', 'received'] as const;
export type ArchiveStatus = (typeof ARCHIVE_STATUSES)[number];

/** The word a person reads for each, on the Archive and in its export. */
export const ARCHIVE_STATUS_LABEL: Record<ArchiveStatus, string> = {
  shipped: 'Shipped',
  received: 'Received',
};

/** Which of the two a case that has left is, from whether the customer has it. */
export function archiveStatusOf(deliveredToCustomerAt: string | null): ArchiveStatus {
  return deliveredToCustomerAt === null ? 'shipped' : 'received';
}

/**
 * The archive's Longest in Shipped order, as `GET /archive?sort=` takes it. A
 * named order like the three origin sorts and not a column, so a leading minus
 * changes nothing: the cases still out come first, the one that has been out
 * longest at the top, then the received ones, the slowest delivery first.
 * Sorted as a plain number, a delivery that took three weeks last spring would
 * stand above a parcel that is late today.
 */
export const SHIPPED_DAYS_SORT = 'shippedDays';

/**
 * After how many whole lab days in Shipped a delivery reads as delayed. One
 * figure for every origin, and a constant rather than a setting until the lab
 * asks for one. The request of 5 October 2026 (third), section 11.
 */
export const SHIPPED_LATE_DAYS = 7;

/**
 * How long a case has been in Shipped, in whole days on the lab's calendar:
 * from the day it left to the day the customer had it, or to today while
 * nobody has marked it received. It runs while the case is Shipped and stands
 * still once it is Received. Never negative, so a row whose dates an old build
 * wrote the wrong way round reads 0. The API's ShippingRules.DaysInShipped,
 * which is what every archive row's `daysInShipped` is.
 */
export function daysInShipped(
  dispatchedAt: string,
  deliveredAt: string | null,
  now: string | number | Date,
): number {
  const left = Date.parse(`${labDayOf(dispatchedAt)}T00:00:00Z`);
  const until = Date.parse(`${labDayOf(deliveredAt ?? now)}T00:00:00Z`);
  return Math.max(0, Math.round((until - left) / 86_400_000));
}

/**
 * How a count of days in Shipped reads, in the `StatusDot` vocabulary. A case
 * still out is late once `SHIPPED_LATE_DAYS` whole lab days have gone by, and
 * that is the only row to stand out: a received case is finished business
 * however long its delivery took, because nobody can hurry it now.
 */
export function shippedDelay(days: number, received: boolean): Tone {
  if (received) return 'done';
  return days >= SHIPPED_LATE_DAYS ? 'late' : 'ok';
}

/**
 * Whether this person is offered Mark received, and the Undo it leads to: on
 * the shipping screen, on an archive row and on the case record. The request of
 * 5 October 2026 (third), sections 10 and 11: whoever holds `ship` marks, and
 * an accountant kept to their own three screens reads only. The screens are
 * asked as well as the permission because `ship` can be granted to one person,
 * and a confined accountant granted it is still refused the two calls behind
 * the button by the server's gate; such a person sees the Received date or
 * nothing, never the button. An accountant who holds another screen is not
 * confined, and marks like anybody who holds `ship`: the request of
 * 6 October 2026 (fourth). See `confinedToAccounts`.
 */
export function mayMarkReceived(person: {
  role: Role;
  views: readonly ViewKey[];
  permissions: readonly Permission[];
}): boolean {
  return !confinedToAccounts(person.role, person.views) && person.permissions.includes('ship');
}

/**
 * What Lab Notes says of a case after it leaves the lab, read off its shipping
 * and never stored. The API's ShippingRules.ReceivedEvent and
 * CollectedByTheCourier. Never who signed for the case at the clinic: that is
 * identity, and a technician reads these notes.
 */
export const SHIPPING_EVENT = {
  received: 'Received by the customer',
  /** A courier case whose courier was never written down. */
  collectedByTheCourier: 'Collected by the courier',
} as const;

/**
 * What Lab Notes says when a case leaves the lab: the courier that collected
 * it, or the lab's own delivery and who drove it when that was written down.
 * The same facts the activity log records at dispatch, as a sentence of their
 * own. The API's ShippingRules.ShippedEvent. The request of 5 October 2026
 * (third), section 11.
 */
export function shippedEvent(
  method: ShippingMethod,
  carrier: string | null,
  driverName: string | null,
): string {
  if (method === 'lab') {
    const driver = (driverName ?? '').trim();
    return driver === '' ? 'Out for delivery' : `Out for delivery with ${driver}`;
  }

  const named = (carrier ?? '').trim();
  return named === '' ? SHIPPING_EVENT.collectedByTheCourier : `Collected by ${named}`;
}

/** The dispatch block on a job order. Every field but `dispatched` and `status` is null until it is known. */
export interface Shipping {
  /** True from the moment it leaves the lab, whichever route. Drives orderState. */
  dispatched: boolean;
  status: ShippingStatus;
  method: ShippingMethod | null;
  /** The courier. Null on a lab delivery. */
  carrier: string | null;
  /** Typed by hand from the waybill, never generated. Null until it arrives. */
  trackingCode: string | null;
  /** Who drove it, on a lab delivery. */
  driverName: string | null;
  note: string | null;
  /** When it left the lab. The billing date, and what the archive sorts by. */
  dispatchedAt: string | null;
  /** When the code was entered, so the lab can see how long the courier took. */
  trackedAt: string | null;
  /** When the customer had it. Null until somebody ticks it. */
  deliveredAt: string | null;
  /**
   * Who signed for it. Null until then, and for a caller without seeIdentity,
   * because the person who signs at a clinic may be the doctor or the patient.
   */
  receivedBy: string | null;
  /**
   * When somebody in the lab marked it received, and who: the moment the mark
   * was made, which the day the customer had it need not be. Null until the
   * case is marked, once the mark is taken back, and on a case received before
   * the two were kept; absent from an API older than they are. Lab staff, not
   * identity, so every caller receives them. The request of 5 October 2026
   * (third), section 11.
   */
  receivedMarkedAt?: string | null;
  receivedMarkedByUserId?: string | null;
  /** The name that person carries today. */
  receivedMarkedByUserName?: string | null;
}

export const NOT_DISPATCHED: Shipping = {
  dispatched: false,
  status: 'pending',
  method: null,
  carrier: null,
  trackingCode: null,
  driverName: null,
  note: null,
  dispatchedAt: null,
  trackedAt: null,
  deliveredAt: null,
  receivedBy: null,
  receivedMarkedAt: null,
  receivedMarkedByUserId: null,
  receivedMarkedByUserName: null,
};

/**
 * Where a case stands on its way to the customer, from what has been recorded.
 * One that has not left is pending, whatever else is filled in. Received
 * outranks the rest. A lab delivery never waits for a code; a courier case waits
 * for one until it is typed in. A case sent before there were methods went by
 * courier, because that was the only way the system knew.
 */
export function shippingStatus(input: {
  dispatched: boolean;
  method: ShippingMethod | null;
  trackingCode: string | null;
  deliveredAt: string | null;
}): ShippingStatus {
  if (!input.dispatched) return 'pending';
  if (input.deliveredAt !== null) return 'delivered';
  if (input.method === 'lab') return 'in_transit';
  return input.trackingCode === null ? 'awaiting_tracking' : 'in_transit';
}

/**
 * What a case's shipping says at a glance, for anybody reading a row. Section 7
 * of the request of 13 September 2026.
 *
 * One function in the domain so six screens cannot disagree: the shipping
 * screen, the orders list, the archive, the logistics column, the case record
 * and the order drawer all read this, and "shipped" means one thing everywhere.
 *
 * The hold is tested FIRST, for the same reason `orderState` tests it first: a
 * hold outranks everything, and a held case that reads "Pending dispatch"
 * would send somebody looking for a van that is never coming.
 *
 * The tones are the `StatusDot` vocabulary, so they mean here what they mean on
 * every other row in the system.
 */
export interface ShippingBadge {
  /** One of `SHIPPING_BADGES`, so a screen can key its own copy off it. */
  key: ShippingBadgeKey;
  tone: Tone;
}

export const SHIPPING_BADGE_KEYS = [
  'onHold',
  'pendingDispatch',
  'awaitingWaybill',
  'shipped',
  'outForDelivery',
  'received',
  /** Still in production, or ready and waiting on accounts: nothing to say about shipping yet. */
  'notYet',
] as const;

export type ShippingBadgeKey = (typeof SHIPPING_BADGE_KEYS)[number];

const BADGE_TONE: Record<ShippingBadgeKey, Tone> = {
  onHold: 'hold',
  pendingDispatch: 'watch',
  awaitingWaybill: 'watch',
  shipped: 'ok',
  outForDelivery: 'ok',
  received: 'done',
  notYet: 'watch',
};

export function shippingBadge(order: {
  state: OrderState;
  dispatch: Pick<Shipping, 'status' | 'method'>;
}): ShippingBadge {
  const key = badgeKey(order);
  return { key, tone: BADGE_TONE[key] };
}

function badgeKey(order: {
  state: OrderState;
  dispatch: Pick<Shipping, 'status' | 'method'>;
}): ShippingBadgeKey {
  // A hold outranks everything, whatever else is true about the case.
  if (order.state === 'held') return 'onHold';
  if (order.state === 'paid') return 'pendingDispatch';
  if (order.state !== 'dispatched') return 'notYet';

  switch (order.dispatch.status) {
    case 'delivered':
      return 'received';
    case 'awaiting_tracking':
      return 'awaitingWaybill';
    case 'in_transit':
      // The lab's own van is out for delivery; a courier has shipped it.
      return order.dispatch.method === 'lab' ? 'outForDelivery' : 'shipped';
    case 'pending':
      // Dispatched with no route recorded: a case that left before the lab
      // chose between a courier and its own van. It says nothing rather than
      // guessing which it was.
      return 'notYet';
  }
}

/**
 * The same badge for a case that has already left, from what an archive row
 * carries: no state and no dispatch block, only the carrier, the code and
 * whether anybody marked it received.
 *
 * A row with no carrier went in the lab's own van, which is the only way a
 * dispatched case has no courier. Built on `shippingStatus` and `shippingBadge`
 * rather than on a second set of rules, so the archive and the shipping screen
 * cannot drift.
 */
export function archiveShippingBadge(row: {
  deliveredToCustomerAt: string | null;
  carrier: string | null;
  trackingCode: string | null;
}): ShippingBadge {
  const method: ShippingMethod = row.carrier === null ? 'lab' : 'courier';
  const status = shippingStatus({
    dispatched: true,
    method,
    trackingCode: row.trackingCode,
    deliveredAt: row.deliveredToCustomerAt,
  });
  return shippingBadge({ state: 'dispatched', dispatch: { status, method } });
}

/** A received instant earlier than the hand-over. Either missing is not a contradiction. */
export function arrivesBeforeLeaves(dispatchedAt: string | null, deliveredAt: string | null): boolean {
  if (dispatchedAt === null || deliveredAt === null) return false;
  return Date.parse(deliveredAt) < Date.parse(dispatchedAt);
}

export type ShippingField = 'carrier' | 'carrierOther' | 'trackingCode' | 'deliveredAt';

/**
 * What is wrong with a dispatch, or with a case's shipping as an edit would
 * leave it, keyed by the field the server names in `errors`. Empty when nothing
 * is. The code is optional; typed, it is four characters at least, and a lab
 * delivery may not carry one at all.
 */
export function shippingFieldErrors(input: {
  method: ShippingMethod | null;
  carrier: string | null;
  /** The name typed beside `Other`. Section 8.2 of the request of 16 September 2026. */
  carrierOther?: string | null;
  trackingCode: string | null;
  dispatchedAt: string | null;
  deliveredAt: string | null;
}): Partial<Record<ShippingField, string>> {
  const errors: Partial<Record<ShippingField, string>> = {};

  if (input.method === 'courier' && (input.carrier === null || input.carrier.trim() === '')) {
    errors.carrier = SHIPPING_REFUSAL.chooseCourier;
  }

  // A courier that is not one of the four has to be named, or the case goes out
  // by "Other", which is nothing anybody can chase or report on.
  if (input.method === 'courier' && input.carrier === CARRIER_OTHER) {
    const named = (input.carrierOther ?? '').trim();
    if (named === '') errors.carrierOther = SHIPPING_REFUSAL.nameTheCourier;
    else if (named.length < CARRIER_OTHER_MIN) {
      errors.carrierOther = SHIPPING_REFUSAL.carrierNameTooShort;
    } else if (named.length > CARRIER_OTHER_MAX) {
      errors.carrierOther = SHIPPING_REFUSAL.carrierNameTooLong;
    }
  }

  if (input.trackingCode !== null) {
    if (input.method === 'lab') errors.trackingCode = SHIPPING_REFUSAL.labHasNoCode;
    else if (input.trackingCode.trim().length < WAYBILL_CODE_MIN) {
      errors.trackingCode = SHIPPING_REFUSAL.codeTooShort;
    }
  }

  if (arrivesBeforeLeaves(input.dispatchedAt, input.deliveredAt)) {
    errors.deliveredAt = SHIPPING_REFUSAL.arrivesBeforeLeaves;
  }

  return errors;
}

/**
 * The instant a day chosen on a shipping dialog stands for. Today is now, so a
 * hand-over entered as it happens keeps its time. An earlier day is midday in
 * the lab, which is the same date in Dubai and in UTC; Dubai keeps no daylight
 * saving, so that is eight in the morning UTC.
 */
export function instantOfLabDay(day: string, today: string, now: string): string {
  return day === today ? now : `${day}T08:00:00Z`;
}

/**
 * The groups of the shipping screen, in the order it shows them: a tab each
 * since the lab's request of 22 October 2026, in the order a case passes
 * through them, from waiting for Accounts to received, and then On hold, which
 * stops a case at any point before it leaves.
 */
export const SHIPPING_GROUPS = [
  'awaiting_payment',
  'ready',
  'awaiting_tracking',
  'in_transit',
  'delivered',
  'held',
] as const;
export type ShippingGroup = (typeof SHIPPING_GROUPS)[number];

/** The tab the screen opens on: the cases the desk dispatches. */
export const DEFAULT_SHIPPING_TAB: ShippingGroup = 'ready';

/** The tab an address names, or the one the screen opens on for anything else. */
export function shippingTabOf(value: string | null): ShippingGroup {
  return SHIPPING_GROUPS.find((group) => group === value) ?? DEFAULT_SHIPPING_TAB;
}

/** Whether the cases of a group have left the lab, and so went with a courier or the lab's van. */
export function hasLeft(group: ShippingGroup): boolean {
  return group === 'awaiting_tracking' || group === 'in_transit' || group === 'delivered';
}

/**
 * The group a case sits in on the shipping screen, or null when it is none of
 * the desk's business: still in production, or received more than fourteen lab
 * days ago. `dayOf` reads an instant as the lab's calendar day.
 */
export function shippingGroup(
  order: { state: OrderState; dispatch: Pick<Shipping, 'status' | 'deliveredAt'> },
  today: string,
  dayOf: (instant: string) => string,
): ShippingGroup | null {
  switch (order.state) {
    case 'production':
      return null;
    case 'ready_deliver':
      return 'awaiting_payment';
    case 'held':
      return 'held';
    case 'paid':
      return 'ready';
    case 'dispatched':
      break;
  }

  const { status, deliveredAt } = order.dispatch;
  if (status === 'awaiting_tracking') return 'awaiting_tracking';
  if (status !== 'delivered') return 'in_transit';
  if (deliveredAt === null) return null;
  return dayOf(deliveredAt) >= daysBefore(today, DELIVERED_SHOWN_DAYS - 1) ? 'delivered' : null;
}
